import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useSessionStore } from "@/stores/session";
import AppLayout from "@/layouts/AppLayout.vue";

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    group?: string;
    permission?: string;
    permissions?: string[];
    public?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: { title: "登录", public: true },
  },
  {
    path: "/",
    component: AppLayout,
    redirect: "/workbench",
    children: [
      {
        path: "workbench",
        component: () => import("@/views/DashboardView.vue"),
        meta: { title: "工作台", group: "概览" },
      },
      {
        path: "assistant",
        component: () => import("@/views/assistant/AssistantView.vue"),
        meta: { title: "etax 办税助手", group: "概览", permission: "assistant:read" },
      },
      {
        path: "invoices",
        component: () => import("@/views/invoices/InvoiceListView.vue"),
        meta: { title: "发票台账", group: "发票业务", permission: "invoice:read" },
      },
      {
        path: "invoices/:id",
        component: () => import("@/views/invoices/InvoiceDetailView.vue"),
        meta: { title: "发票详情", group: "发票业务", permission: "invoice:read" },
      },
      {
        path: "imports",
        component: () => import("@/views/imports/ImportListView.vue"),
        meta: { title: "发票导入", group: "发票业务", permissions: ["invoice:import", "invoice:review"] },
      },
      {
        path: "imports/:id",
        component: () => import("@/views/imports/ImportDetailView.vue"),
        meta: { title: "导入批次", group: "发票业务", permissions: ["invoice:import", "invoice:review"] },
      },
      {
        path: "invoice-requests",
        component: () => import("@/views/requests/InvoiceRequestsView.vue"),
        meta: { title: "开票申请", group: "发票业务" },
      },
      {
        path: "bills",
        component: () => import("@/views/bills/BillsView.vue"),
        meta: { title: "账单与分摊", group: "账务协作", permission: "bill:write" },
      },
      {
        path: "projects",
        component: () => import("@/views/projects/ProjectsView.vue"),
        meta: { title: "项目管理", group: "账务协作", permission: "project:read" },
      },
      {
        path: "fixed-assets",
        component: () => import("@/views/assets/AssetListView.vue"),
        meta: { title: "固定资产", group: "账务协作", permission: "asset:read" },
      },
      {
        path: "fixed-assets/:id",
        component: () => import("@/views/assets/AssetDetailView.vue"),
        meta: { title: "资产详情", group: "账务协作", permission: "asset:read" },
      },
      {
        path: "reimbursements",
        component: () => import("@/views/reimbursements/ReimbursementListView.vue"),
        meta: { title: "公司报销", group: "账务协作", permission: "reimbursement:read" },
      },
      {
        path: "reimbursements/:id",
        component: () => import("@/views/reimbursements/ReimbursementDetailView.vue"),
        meta: { title: "报销详情", group: "账务协作", permission: "reimbursement:read" },
      },
      {
        path: "receivables",
        component: () => import("@/views/receivables/ReceivablesView.vue"),
        meta: { title: "应收台账", group: "账务协作", permission: "receivable:read" },
      },
      {
        path: "receivables/:id",
        component: () => import("@/views/receivables/ReceivableDetailView.vue"),
        meta: { title: "应收详情", group: "账务协作", permission: "receivable:read" },
      },
      {
        path: "bank-receipts",
        component: () => import("@/views/bank/BankReceiptsView.vue"),
        meta: { title: "银行回单", group: "账务协作", permission: "funds:read" },
      },
      {
        path: "funds",
        component: () => import("@/views/funds/FundsView.vue"),
        meta: { title: "资金核对", group: "账务协作", permission: "funds:read" },
      },
      {
        path: "bill-imports/:id",
        component: () => import("@/views/bills/BillImportDetailView.vue"),
        meta: { title: "账单导入预览", group: "账务协作", permission: "bill:write" },
      },
      {
        path: "contracts",
        component: () => import("@/views/contracts/ContractListView.vue"),
        meta: { title: "合同管理", group: "账务协作" },
      },
      {
        path: "contracts/:id",
        component: () => import("@/views/contracts/ContractDetailView.vue"),
        meta: { title: "合同详情", group: "账务协作" },
      },
      {
        path: "finance",
        component: () => import("@/views/finance/FinanceView.vue"),
        meta: { title: "财务结账", group: "账务协作", permission: "invoice:read" },
      },
      {
        path: "tax-preparations",
        component: () => import("@/views/finance/TaxPreparationsView.vue"),
        meta: { title: "申报与文书", group: "账务协作", permission: "invoice:read" },
      },
      {
        path: "tax-certificates",
        component: () => import("@/views/finance/TaxCertificatesView.vue"),
        meta: { title: "完税证明", group: "账务协作", permission: "tax-certificate:read" },
      },
      {
        path: "tax-tasks",
        component: () => import("@/views/tasks/TaxTasksView.vue"),
        meta: { title: "办税事项", group: "账务协作" },
      },
      {
        path: "files",
        component: () => import("@/views/files/FileCenterView.vue"),
        meta: { title: "文件中心", group: "资料与记录", permission: "file:download" },
      },
      {
        path: "exports",
        component: () => import("@/views/exports/ExportsView.vue"),
        meta: { title: "导出任务", group: "资料与记录", permission: "invoice:export" },
      },
      {
        path: "notifications",
        component: () => import("@/views/NotificationsView.vue"),
        meta: { title: "通知中心", group: "资料与记录" },
      },
      {
        path: "tax-logs",
        component: () => import("@/views/tax-logs/TaxLogsView.vue"),
        meta: { title: "办税日志", group: "资料与记录", permission: "tax-log:read" },
      },
      {
        path: "audit-logs",
        component: () => import("@/views/AuditLogsView.vue"),
        meta: { title: "审计日志", group: "资料与记录", permission: "audit:read" },
      },
      {
        path: "business-license",
        component: () => import("@/views/company/BusinessLicenseView.vue"),
        meta: { title: "营业执照", group: "企业管理", permission: "file:download" },
      },
      {
        path: "personnel",
        component: () => import("@/views/personnel/PersonnelView.vue"),
        meta: { title: "受雇人员", group: "企业管理", permission: "personnel:read" },
      },
      {
        path: "settings",
        component: () => import("@/views/settings/SettingsView.vue"),
        meta: { title: "企业设置", group: "企业管理" },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/views/NotFoundView.vue"),
    meta: { public: true, title: "页面不存在" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const session = useSessionStore();
  if (!session.initialized && !session.isAuthenticated) await session.restore();
  if (!to.meta.public && !session.isAuthenticated)
    return { name: "login", query: { redirect: to.fullPath } };
  if (to.name === "login" && session.isAuthenticated) return "/workbench";
  if (to.meta.permission && !session.can(to.meta.permission))
    return "/workbench";
  if (to.meta.permissions && !session.canAny(...to.meta.permissions))
    return "/workbench";
  document.title = `${to.meta.title ?? "工作台"} · 财务管理 · 云比特`;
  return true;
});

export default router;
